import React from "react";
import type { MetricItem } from "services/apiDashboard";

interface MetricCardProps {
  title: string;
  metric?: MetricItem;
  icon?: React.ReactNode;
  loading?: boolean;
  tooltipText?: string;
  updatedAt?: string;
  variant?: "positive" | "warning" | "neutral" | "danger";
}

/**
 * Card de métrica / KPI individual para el Dashboard.
 * Diseño compacto: Título, valor destacado, indicador de tendencia (+12%), sparkline visual y fecha.
 */
export const MetricCard: React.FC<MetricCardProps> = ({
  title,
  metric,
  icon,
  loading = false,
  tooltipText,
  updatedAt,
  variant = "positive",
}) => {
  if (loading) {
    return (
      <div className="bg-white rounded-2xl p-5 border border-gray-200/80 shadow-sm animate-pulse space-y-3">
        <div className="flex justify-between items-center">
          <div className="h-3 bg-gray-200 rounded w-24" />
          <div className="h-6 w-6 bg-gray-200 rounded-full" />
        </div>
        <div className="h-8 bg-gray-200 rounded w-16" />
        <div className="h-2 bg-gray-100 rounded w-full" />
      </div>
    );
  }

  const value = metric?.value ?? 0;
  const variation = metric?.variation;
  const isPositive = variation != null && variation >= 0;

  const getVariantStyles = () => {
    switch (variant) {
      case "danger":
        return {
          badgeBg: "bg-red-50 text-red-700 border-red-200",
          barColor: "bg-red-500",
        };
      case "warning":
        return {
          badgeBg: "bg-amber-50 text-amber-700 border-amber-200",
          barColor: "bg-amber-500",
        };
      case "neutral":
        return {
          badgeBg: "bg-blue-50 text-blue-700 border-blue-200",
          barColor: "bg-blue-500",
        };
      default:
        return {
          badgeBg: "bg-emerald-50 text-emerald-700 border-emerald-200",
          barColor: "bg-emerald-500",
        };
    }
  };

  const style = getVariantStyles();

  return (
    <div
      title={tooltipText}
      className="bg-white rounded-2xl p-5 border border-gray-200/80 shadow-sm hover:shadow-md transition-shadow flex flex-col justify-between"
    >
      <div>
        {/* Top: Title & Icon */}
        <div className="flex items-center justify-between gap-2 mb-2">
          <span className="text-xs font-bold uppercase tracking-wider text-gray-400">
            {title}
          </span>
          {icon && <div className="text-gray-400 shrink-0">{icon}</div>}
        </div>

        {/* Middle: Value & Trend */}
        <div className="flex items-baseline justify-between gap-2">
          <div className="text-3xl font-extrabold text-gray-900 tracking-tight">
            {value.toLocaleString()}
          </div>
          {variation != null && (
            <span
              className={`inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-xs font-bold border ${
                isPositive ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-red-50 text-red-700 border-red-200"
              }`}
            >
              {isPositive ? "↑" : "↓"} {Math.abs(variation)}%
            </span>
          )}
        </div>
      </div>

      {/* Bottom: Sparkline visual y Timestamp */}
      <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between">
        {/* Sparkline mini bars */}
        <div className="flex items-end gap-1 h-3">
          <div className={`w-1 h-2 rounded-full ${style.barColor} opacity-40`} />
          <div className={`w-1 h-3 rounded-full ${style.barColor} opacity-60`} />
          <div className={`w-1 h-2.5 rounded-full ${style.barColor} opacity-80`} />
          <div className={`w-1 h-3.5 rounded-full ${style.barColor}`} />
        </div>
        {updatedAt && (
          <span className="text-[10px] text-gray-400">Hoy</span>
        )}
      </div>
    </div>
  );
};

export default MetricCard;
